import { useCallback, useRef } from 'react';

import { useAsyncThrottler } from '@tanstack/react-pacer/async-throttler';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys, type Session } from '@/entities/session';
import {
    AUTOSAVE_THROTTLE,
    type ContentByType,
    useWriteNoteOptions,
    type WriteSessionNoteInput,
} from '@/entities/session-note';

export function useSessionNoteAutosave(sessionId: string) {
    const queryClient = useQueryClient();

    const { mutateAsync: writeNote } = useMutation(useWriteNoteOptions(sessionId));

    const throttler = useAsyncThrottler(
        async <T extends keyof ContentByType>(type: T, content: ContentByType[T]) => {
            const queryKey = queryKeys.detail(sessionId);
            const id = queryClient.getQueryData<Session>(queryKey)?.currentNote?.id;
            await writeNote({ id, sessionId, type, content } as WriteSessionNoteInput);
        },
        { wait: AUTOSAVE_THROTTLE, leading: false, trailing: true },
        (state) => ({ isPending: state.isPending, isExecuting: state.isExecuting }),
    );

    const throttlerRef = useRef(throttler);
    throttlerRef.current = throttler;

    // React Compiler doesn't memoize these generic callbacks reliably, so
    // `saveContent`'s identity would change on every render and re-render every
    // note section component on each keystroke. Keep the manual useCallback.
    const applyOptimisticUpdate = useCallback(
        <T extends keyof ContentByType>(type: T, content: ContentByType[T]) => {
            const queryKey = queryKeys.detail(sessionId);
            const session = queryClient.getQueryData<Session>(queryKey);
            if (!session?.currentNote || session.currentNote.type !== type) return;
            queryClient.setQueryData<Session>(queryKey, {
                ...session,
                currentNote: {
                    ...session.currentNote,
                    content,
                } as NonNullable<Session['currentNote']>,
            });
        },
        [sessionId, queryClient],
    );

    const saveContent = useCallback(
        <T extends keyof ContentByType>(type: T, content: ContentByType[T]) => {
            applyOptimisticUpdate(type, content);
            throttlerRef.current.maybeExecute(type, content);
        },
        [applyOptimisticUpdate],
    );

    return {
        saveContent,
        isContentSaving: throttler.state.isPending || throttler.state.isExecuting,
    };
}
